import { MailFilled } from "@ant-design/icons";
import { Divider, message } from "antd";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom";
import { verifyToken } from "../../apis/loginApi";
import { ErrorIcon, VerifiedIcon } from "../../common/icons";
import { loginStores } from "../Login/LoginStores";
import "./verification.css";
import { useTranslation } from "react-i18next";

const STATUS = {
  loading: "loading",
  success: "success",
  fail: "fail",
};

const COLOR = {
  [STATUS.loading]: "#5e72e4",
  [STATUS.success]: "#52c41a",
  [STATUS.fail]: "#f5365c",
};

const Verification = observer(() => {
  const [status, setStatus] = useState(STATUS.loading);
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();

  const MESSAGE = {
    [STATUS.loading]: { title: t("We are currently verifying your account."), description: t("Please wait a moment") },
    [STATUS.success]: {
      title: t("Your account has been successfully verified."),
      description: t("Redirecting to the homepage"),
    },
    [STATUS.fail]: {
      title: t("An error occurred while verifying your account."),
      description: t("Please sign up again"),
    },
  };

  const verify = useCallback(async () => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token");
    if (token) {
      try {
        const response = await verifyToken(token);
        if (response) {
          setTimeout(() => {
            loginStores.initUserInfo(response);
            setStatus(STATUS.success);
            setTimeout(() => {
              history.push("/");
            }, 2000);
          }, 2000);
        }
      } catch (error) {
        setTimeout(() => {
          setStatus(STATUS.fail);
          if (error?.data?.responseText) message.error(t(error?.data?.responseText));
          setTimeout(() => {
            history.push("/register");
          }, 5000);
        }, 2000);
      }
    } else {
      history.push("/");
    }
  }, [history, location.search, t]);

  useEffect(() => {
    verify();
  }, [verify]);

  return (
    <div className="h-screen flex-center">
      <div className="overflow-visible w-full h-full sm:w-3/5 sm:h-4/5 lg:w-2/5">
        <div className="verification-content">
          <div className="verification-back">
            <div
              className="verification-spinner"
              style={{
                animation:
                  status === STATUS.loading
                    ? "rotation-verification 3000ms linear infinite"
                    : "rotation-verification 3000ms linear infinite, expand-verification 500ms ease-in-out forwards",
                background: COLOR[status],
              }}
            ></div>
            <div className="verification-back-content">
              <div className="h-1/4 w-full flex flex-col items-center justify-start gap-1">
                <div className="text-4xl font-semibold">
                  <span style={{ color: status === STATUS.loading ? "#296FF9" : COLOR[status] }}>Airflex</span>
                  <span style={{ color: status === STATUS.loading ? "#9618F7" : COLOR[status] }}>.io</span>
                </div>
                <div className="p-2 rounded-xl w-5/6 text-center">
                  <Divider className="!px-14 mt-0 mb-2" style={{ borderColor: COLOR[status] }}>
                    <MailFilled className="text-2xl" style={{ color: COLOR[status] }} />
                  </Divider>
                  <span className="font-semibold">{t("Account verification")}</span>
                </div>
              </div>
              <img
                className="verification-logo"
                src="/assets/img/brand/logo_icon.png"
                width={60}
                alt=""
                style={{
                  animation:
                    status === STATUS.loading
                      ? "rotation-verification 3000ms linear infinite"
                      : "shrink-verification 0.5s forwards",
                }}
              />
              {status === STATUS.success && (
                <div className="absolute" style={{ animation: "grow-verification 0.5s forwards" }}>
                  <VerifiedIcon fill="#52c41a" size={80} />
                </div>
              )}
              {status === STATUS.fail && (
                <div className="absolute" style={{ animation: "grow-verification 0.5s forwards" }}>
                  <ErrorIcon fill="#f5365c" size={80} />
                </div>
              )}
              <div className="h-1/4 w-full flex-center flex-col">
                <span>{MESSAGE[status].title}</span>
                <span className="flex items-center gap-4">
                  <span className="text-sm text-tgray font-light">{MESSAGE[status].description}</span>
                  <div className="dot-flashing mt-1.5"></div>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Verification;
